import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { LogIn, Loader2, Mail, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { useAuth } from "@/auth/AuthContext";
import { api, formatApiError } from "@/api/client";
import { LOGIN } from "@/constants/testIds";

export default function LoginPage() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try { await login(email, password); nav("/", { replace: true }); }
    catch (err) { setError(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-slate-950 flex items-center justify-center px-4">
      {/* Yüzen gradient orb'lar — yavaş animasyon */}
      <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-emerald-500/40 to-teal-500/20 blur-3xl animate-pulse-slow"/>
      <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-blue-500/40 to-indigo-500/20 blur-3xl animate-pulse-slow"
           style={{ animationDelay: "2s" }}/>
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[400px] h-[400px] rounded-full bg-gradient-to-br from-purple-500/30 to-pink-500/10 blur-3xl animate-pulse-slow"
           style={{ animationDelay: "4s" }}/>

      {/* İnce grid dokusu */}
      <div className="absolute inset-0 opacity-[0.04] [background-image:linear-gradient(rgba(255,255,255,0.5)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.5)_1px,transparent_1px)] [background-size:40px_40px] pointer-events-none"/>

      {/* Login card */}
      <form onSubmit={submit}
            className="relative w-full max-w-md rounded-3xl bg-white/[0.04] border border-white/10 backdrop-blur-2xl p-10 shadow-2xl shadow-black/50"
            data-testid="login-form">
        {/* Üst köşe accent line */}
        <div className="absolute top-0 left-8 right-8 h-px bg-gradient-to-r from-transparent via-emerald-400/60 to-transparent"/>

        {/* Logo — büyük ve merkezde */}
        <div className="flex justify-center mb-8">
          <div className="relative">
            <div className="absolute inset-0 bg-emerald-400/20 blur-2xl rounded-full"/>
            <img src="/logo.jpg" alt="Has Yapı"
                 className="relative h-20 w-auto object-contain rounded-2xl bg-white p-2 shadow-2xl"
                 data-testid="brand-logo"/>
          </div>
        </div>

        {error && (
          <Alert variant="destructive" data-testid="login-error"
                 className="mb-6 bg-red-950/40 border-red-500/30 text-red-200">
            <AlertDescription className="text-sm">{error}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-slate-400 text-xs uppercase tracking-widest font-semibold">
              E-posta
            </Label>
            <Input id="email" type="email" required
                   value={email} onChange={(e) => setEmail(e.target.value)}
                   data-testid={LOGIN.emailInput}
                   autoFocus autoComplete="username"
                   placeholder="ornek@hasyapimarket.com"
                   className="h-12 bg-white/5 border-white/10 text-slate-100 placeholder:text-slate-500 focus:bg-white/10 focus:border-emerald-400/60 focus:ring-emerald-400/20 rounded-xl transition-all"/>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password" className="text-slate-400 text-xs uppercase tracking-widest font-semibold">
                Şifre
              </Label>
              <button type="button"
                      onClick={() => setForgotOpen(true)}
                      data-testid="login-forgot-password-btn"
                      className="text-[11px] text-slate-400 hover:text-emerald-300 transition-colors">
                Şifremi unuttum?
              </button>
            </div>
            <Input id="password" type="password" required
                   value={password} onChange={(e) => setPassword(e.target.value)}
                   data-testid={LOGIN.passwordInput}
                   autoComplete="current-password"
                   placeholder="••••••••"
                   className="h-12 bg-white/5 border-white/10 text-slate-100 placeholder:text-slate-500 focus:bg-white/10 focus:border-emerald-400/60 focus:ring-emerald-400/20 rounded-xl transition-all"/>
          </div>
        </div>

        <Button type="submit"
                className="w-full h-12 mt-8 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 hover:from-emerald-400 hover:via-teal-400 hover:to-cyan-400 text-slate-950 font-semibold rounded-xl shadow-xl shadow-emerald-500/30 hover:shadow-emerald-500/50 hover:-translate-y-0.5 active:translate-y-0 transition-all"
                disabled={busy}
                data-testid={LOGIN.submitButton}>
          {busy ? (
            <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Giriş yapılıyor...</>
          ) : (
            <><LogIn className="h-5 w-5 mr-2" /> Giriş Yap</>
          )}
        </Button>

        <div className="mt-6 text-center">
          <a href="/register"
             className="text-xs text-slate-400 hover:text-emerald-300 transition-colors"
             data-testid="login-goto-register">
            Hesabınız yok mu? <span className="text-emerald-400 font-medium">Kayıt olun</span>
          </a>
        </div>
      </form>

      <ForgotPasswordDialog open={forgotOpen} onClose={() => setForgotOpen(false)}
                             defaultEmail={email}/>
    </div>
  );
}

// Şifre sıfırlama modal — kullanıcı e-posta girer, backend token üretir (log'a düşer).
// SEC: Sunucu her zaman generic mesaj döndürür (user enumeration engellenir).
function ForgotPasswordDialog({ open, onClose, defaultEmail = "" }) {
  const [email, setEmail] = useState(defaultEmail);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleOpenChange = (o) => {
    if (!o) {
      onClose();
      setTimeout(() => { setSent(false); setError(""); setBusy(false); }, 200);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { setError("E-posta gerekli"); return; }
    setError(""); setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md" data-testid="forgot-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-emerald-500"/>
            Şifre Sıfırlama
          </DialogTitle>
          <DialogDescription>
            {sent
              ? "Eğer bu e-posta sisteme kayıtlıysa, sıfırlama bağlantısı yönetici tarafından iletilecektir."
              : "Hesabınıza kayıtlı e-posta adresinizi girin. Şifre sıfırlama isteği oluşturulacak."}
          </DialogDescription>
        </DialogHeader>

        {!sent ? (
          <form onSubmit={submit} className="space-y-4">
            {error && (
              <Alert variant="destructive" data-testid="forgot-error">
                <AlertDescription className="text-sm">{error}</AlertDescription>
              </Alert>
            )}
            <div className="space-y-2">
              <Label htmlFor="forgot-email">E-posta</Label>
              <Input id="forgot-email" type="email" required autoFocus
                     value={email} onChange={(e) => setEmail(e.target.value)}
                     placeholder="ornek@hasyapimarket.com"
                     data-testid="forgot-email"/>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={onClose}>İptal</Button>
              <Button type="submit" disabled={busy} data-testid="forgot-submit"
                      className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-slate-950 font-semibold">
                {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin"/>Gönderiliyor...</>
                       : <><Mail className="h-4 w-4 mr-2"/>Sıfırlama Bağlantısı Gönder</>}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <div className="space-y-4" data-testid="forgot-success">
            <Alert className="bg-emerald-50 dark:bg-emerald-950/30 border-emerald-500/30">
              <AlertDescription className="text-sm">
                Talebiniz alındı. Kısa süre içinde yönetici sizinle iletişime geçecek veya e-posta gelecektir.
                Gelmediyse spam klasörünü kontrol edin.
              </AlertDescription>
            </Alert>
            <DialogFooter>
              <Button onClick={onClose} data-testid="forgot-close">Tamam</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
